import { AuthProviders } from './auth-providers.js';
import { OpenIDProvider } from './open-id-provider.js';
import { TokenProvider } from './token-provider.js';
import { object } from '../typing/object.js';
import { string } from '../typing/string.js';
import { record } from '../typing/record.js';
import { union } from '../typing/union.js';
import { literal } from '../typing/literal.js';
import { optional } from '../typing/optional.js';
import { TypeOf } from '../typing/type-of.js';

export const PublicJwtClientSchema = object('PublicJwtClient', {
  type: literal('jwt'),
});

export const PublicOpenIDClientSchema = object('PublicOpenIDClient', {
  type: literal('openid'),
  authorize: optional(
    object('PublicOpenIDAuthorize', {
      url: string(),
      queryParams: record(string(), string()),
    }),
  ),
});

export const PublicTokenClientSchema = object('PublicTokenClient', {
  type: literal('token'),
  authorize: optional(
    object('PublicTokenAuthorize', {
      url: string(),
      queryParams: record(string(), string()),
    }),
  ),
});

export const PublicAuthProvider = union(
  'PublicAuthProvider',
  [PublicOpenIDClientSchema, PublicJwtClientSchema, PublicTokenClientSchema],
  'type',
);

export const PublicAuthProvidersResponseSchema = object(
  'PublicAuthProvidersResponse',
  {
    realm: string(),
    providers: record(string(), PublicAuthProvider),
  },
);

export type PublicAuthProvidersResponse = TypeOf<
  typeof PublicAuthProvidersResponseSchema
>;

export type PublicAuthProvider = TypeOf<typeof PublicAuthProvider>;

function publicOpenIDClient(p: OpenIDProvider): PublicAuthProvider {
  const o: { type: 'openid'; authorize?: OpenIDProvider['authorize'] } = {
    type: 'openid',
  };
  if (p.authorize) {
    o.authorize = p.authorize;
  }
  return o as PublicAuthProvider;
}

function publicTokenClient(p: TokenProvider): PublicAuthProvider {
  const o: { type: 'token'; authorize?: TokenProvider['authorize'] } = {
    type: 'token',
  };
  if (p.authorize) {
    o.authorize = p.authorize;
  }
  return o as PublicAuthProvider;
}

export function serializePublicAuthProviders(
  providers: AuthProviders,
): Record<string, PublicAuthProvider> {
  const out: Record<string, PublicAuthProvider> = {};
  for (const [key, p] of Object.entries(providers)) {
    if (p.type === 'openid') {
      out[key] = publicOpenIDClient(p);
    } else if (p.type === 'jwt') {
      out[key] = { type: 'jwt' };
    } else {
      out[key] = publicTokenClient(p);
    }
  }
  return out;
}
